import * as I from 'immutable';
import React from 'react';

import {api} from 'app/modules/Remote';
import {ApiOrganization} from 'app/modules/Remote/Organization';
import * as C from 'app/utils/constants';
import {useApiGet} from 'app/utils/hookUtils';
import * as routeUtils from 'app/utils/routeUtils';

export function getLensTier(organization: I.ImmutableOf<ApiOrganization>): C.LENS_TIERS_TYPE {
  // If we don't have a tier set assume the lowest
  return organization.getIn(['tiers', 'Lens']) || C.LENS_TIER_FOCUS;
}

export function getIsOrgOverSeatLimit(
  organization: I.ImmutableOf<ApiOrganization>,
  organizationUsersCount: number
): boolean {
  const orgSeatLimit = organization.getIn(['settings', C.ORG_SETTING_SEAT_LIMIT]);
  if (orgSeatLimit) {
    return organizationUsersCount >= orgSeatLimit;
  }

  const orgTier = getLensTier(organization);
  const seatLimit = C.LENS_TIER_SETTINGS[orgTier].seatLimit;
  return !!seatLimit && organizationUsersCount >= seatLimit;
}

export const AT_LEAST_PLUS: C.LENS_TIERS_TYPE[] = [C.LENS_TIER_PLUS, C.LENS_TIER_ENTERPRISE];

// Checks if the provided org's tier is in the provided allowed tiers.
export const orgIsAllowedTier = (
  organization: I.ImmutableOf<ApiOrganization>,
  allowedTiers: C.LENS_TIERS_TYPE | C.LENS_TIERS_TYPE[]
): boolean => {
  const orgTier = getLensTier(organization);
  // We can avoid checking anything if we have as many allowed tiers as defined tiers
  if (C.LENS_TIERS.length === allowedTiers.length) {
    return true;
  }
  // Otherwise, lets see if org tier is in the list of allowed tiers
  if (Array.isArray(allowedTiers)) {
    return allowedTiers.includes(orgTier);
  } else {
    return allowedTiers == orgTier;
  }
};

export const getOrgPrefix = (organization: I.ImmutableOf<ApiOrganization>) =>
  routeUtils.makeUuidPrefix(organization.get('id'));

/**
 * One billing mode covers the whole organization, and only we set it. Unset means invoiced: the
 * organization orders against an imagery contract and is billed monthly.
 */
export const paysAtCheckout = (organization: I.ImmutableOf<ApiOrganization> | null): boolean =>
  organization?.getIn(['settings', C.ORG_SETTING_IMAGERY_BILLING]) === C.IMAGERY_BILLING_CHECKOUT;

// Merges org-level default layers with every layer the org is licensed for under
// each premium source it currently subscribes to, so new portfolios automatically
// enroll the layers the customer is already paying for.
export function mergePremiumSourceLayers(
  defaultLayersBySource: Record<string, string[]>,
  supportedLayersBySource: Record<string, string[]>,
  subscribedPremiumSourceIds: string[]
): Record<string, string[]> {
  const merged: Record<string, string[]> = {...defaultLayersBySource};
  for (const sourceId of subscribedPremiumSourceIds) {
    const supported = supportedLayersBySource[sourceId];
    if (!supported || supported.length === 0) {
      continue;
    }
    merged[sourceId] = Array.from(new Set([...(merged[sourceId] || []), ...supported]));
  }
  return merged;
}

// Hook that returns the layers a new portfolio should enroll by default for this
// org — the org's defaultLayersBySource plus every supported layer under the
// premium sources the org currently subscribes to. Returns undefined while the
// premium-source fetch is in flight.
export function useDefaultLayersBySource(
  organization: I.ImmutableOf<ApiOrganization>
): Record<string, string[]> | undefined {
  const organizationId = organization.get('id');
  const [{value: subscribedPremiumSourceIds}] = useApiGet(
    async (orgId: string) =>
      (await api.organizations.listPremiumSources(orgId)).get('data').toJS() as string[],
    [organizationId]
  );

  const orgDefaultLayersBySource = organization.get('defaultLayersBySource');
  const orgSupportedLayersBySource = organization.get('supportedLayersBySource');

  // Memoize so consumers that pass this into dep arrays (e.g. useStateWithDeps)
  // don't see a fresh object identity on every render and reset their state.
  return React.useMemo(() => {
    if (subscribedPremiumSourceIds === undefined) {
      return undefined;
    }
    return mergePremiumSourceLayers(
      orgDefaultLayersBySource.toJS() as Record<string, string[]>,
      orgSupportedLayersBySource.toJS() as Record<string, string[]>,
      subscribedPremiumSourceIds
    );
  }, [orgDefaultLayersBySource, orgSupportedLayersBySource, subscribedPremiumSourceIds]);
}
